import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../../assets/logo.svg';
import {
  PersonalFormulaReady,
  ProductSuggestionDoses,
  ProductSuggestionImprovementBadge,
  ProductSuggestionIngredientsBadge,
  VeganVCP,
  YourFocusAreasDesc,
} from '../../Constants';
import {
  CLASSMED_SOURCE,
  getClassmedIdFromSearch,
  getClassmedPharmacyIdFromSearch,
  getClassmedQuizPath,
  getClassmedResultPath,
  getStoredClassmedId,
  getStoredClassmedResult,
  saveClassmedPharmacyId,
  saveClassmedResult,
} from '../../utils/classmed';
import { withStaticDescriptions } from '../../utils/productSuggestions';
import { buildMainPurposeSubline } from '../../utils/main-purpose-subline';
import {
  DEFAULT_FOCUS_COLLAPSED_COUNT,
  MAX_PRIORITY_FOCUS_COUNT,
  orderFocusAreasByPriority,
} from '../../utils/focus-area-priority';
import {
  useAnswersMutation,
  useGetAssetsQuery,
  useGetClassmedResultQuery,
} from '../../features/quizApiSlice';
import useClassmedProduct from '../../hooks/useClassmedProduct';
import useReviewsIoWidgetStats from '../../hooks/use-reviews-io-widget-stats';
import AddIngredientModal from '../Customer/components/AddIngredientModal';
import Carousel from '../Customer/components/Carousel';
import EditFormulaModal from '../Customer/components/EditFormulaModal';
import FocusAreaAccordion from '../Customer/components/FocusAreaAccordion';
import FormulaLoader from '../Customer/components/FormulaLoader';
import IngredientsModal from '../Customer/components/IngredientsModal';
import ProductHighlightPill from '../Customer/components/ProductHighlightPill';
import ProductPageSections from '../Customer/components/ProductPageSections';
import Header from '../Customer/product/Header';
import ReviewsOverlay from '../Reviews';
import { Helmet } from 'react-helmet';
import ClassmedOneOffPurchaseCard, { formatClassmedPrice } from './ClassmedOneOffPurchaseCard';

// Reached only through a personal ClassMED link: keep it out of search results.
const noIndexHelmet = (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

const ClassmedResult = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const classmedId = getClassmedIdFromSearch() || getStoredClassmedId();
  const classmedPharmacyId = getClassmedPharmacyIdFromSearch();
  const lang = 'fr';
  const isFreshFromQuiz = Boolean(state?.freshFromQuiz);
  const storedCandidate = useMemo(() => {
    if (state?.result) return state.result;
    if (isFreshFromQuiz) return null;

    const cachedResult = getStoredClassmedResult();
    return String(cachedResult?.classmedUser?.classmedId || '') === String(classmedId || '')
      ? cachedResult
      : null;
  }, [classmedId, isFreshFromQuiz, state?.result]);
  const storedResult = storedCandidate?.classmedResultLang === lang ? storedCandidate : null;
  const freshFromQuizRef = useRef(isFreshFromQuiz);
  const freshQuizStateRef = useRef(isFreshFromQuiz ? state : null);
  const freshQuizFiredRef = useRef(false);
  const reviewsRef = useRef(null);
  const leftContainerRef = useRef(null);
  const rightScrollRef = useRef(null);
  const [submittedResult, setSubmittedResult] = useState(null);
  const [ingredientsModal, setIngredientsModal] = useState(false);
  const [editIngredientsModal, setEditIngredientsModal] = useState(false);
  const [showFormulaLoader, setShowFormulaLoader] = useState(
    Boolean(state?.freshFromQuiz || (!storedResult && classmedId)),
  );
  const [submitAnswers] = useAnswersMutation();
  const { data: focusAreaIcons } = useGetAssetsQuery({ section: 'focusAreaIcon', lang });
  const {
    data: fetchedResult,
    isFetching,
    isError,
  } = useGetClassmedResultQuery(
    { classmedId, lang },
    { skip: Boolean(storedResult || submittedResult || freshQuizStateRef.current || !classmedId) },
  );
  const rawResult = storedResult || submittedResult || fetchedResult;
  const result = useMemo(
    () =>
      rawResult
        ? {
            ...rawResult,
            ingredients: withStaticDescriptions(rawResult.ingredients || []),
          }
        : null,
    [rawResult],
  );
  const product = useClassmedProduct({ result, classmedId, pharmacyId: classmedPharmacyId });
  const reviewStats = useReviewsIoWidgetStats();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [i18n]);

  useEffect(() => {
    saveClassmedPharmacyId(classmedPharmacyId);
  }, [classmedPharmacyId]);

  useEffect(() => {
    if (!fetchedResult) return;
    saveClassmedResult(fetchedResult, classmedId, lang);
  }, [classmedId, fetchedResult]);

  useEffect(() => {
    const freshQuizState = freshQuizStateRef.current;
    if (!freshQuizState || freshQuizFiredRef.current || !classmedId) return;
    freshQuizFiredRef.current = true;

    submitAnswers({
      quiz_id: 0,
      source: CLASSMED_SOURCE,
      classmedId,
      answers: freshQuizState.quizAnswers,
      lang,
      dataJson: freshQuizState.slideValues,
      slideNavigation: freshQuizState.slideNavigation,
      offer: freshQuizState.offer,
    })
      .unwrap()
      .then((response) => {
        const classmedResult = saveClassmedResult(response, classmedId, lang) || response;
        setSubmittedResult(classmedResult);
        freshQuizStateRef.current = null;
      })
      .catch((error) => {
        console.error('Error submitting Classmed quiz:', error);
        freshQuizStateRef.current = null;
        setShowFormulaLoader(false);
      });
  }, [classmedId, submitAnswers]);

  useEffect(() => {
    if (isError) setShowFormulaLoader(false);
  }, [isError]);

  useEffect(() => {
    const leftContainer = leftContainerRef.current;
    const rightContainer = rightScrollRef.current;
    if (!leftContainer || !rightContainer) return undefined;

    const handleLeftWheel = (event) => {
      if (window.innerWidth < 1024 || !leftContainer.contains(event.target)) return;

      const isAtTop = rightContainer.scrollTop <= 0;
      const isAtBottom =
        rightContainer.scrollTop + rightContainer.clientHeight >= rightContainer.scrollHeight - 1;

      if ((isAtTop && event.deltaY < 0) || (isAtBottom && event.deltaY > 0)) return;

      event.preventDefault();
      event.stopPropagation();
      rightContainer.scrollTop += event.deltaY;
    };

    leftContainer.addEventListener('wheel', handleLeftWheel, { passive: false, capture: true });

    return () => {
      leftContainer.removeEventListener('wheel', handleLeftWheel, { capture: true });
    };
  }, [result]);

  const focusArea = useMemo(() => result?.focusArea || [], [result?.focusArea]);
  const orderedFocusArea = useMemo(
    () => orderFocusAreasByPriority(focusArea, result?.priorityFocus || []),
    [focusArea, result?.priorityFocus],
  );
  const collapsedFocusCount = result?.priorityFocus?.length
    ? Math.min(result.priorityFocus.length, MAX_PRIORITY_FOCUS_COUNT)
    : DEFAULT_FOCUS_COLLAPSED_COUNT;
  const name = result?.classmedUser?.name || t('Classmed_DefaultName');
  const firstName = result?.classmedUser?.name?.trim().split(' ')[0] || null;
  const mainPurposeSubline = useMemo(
    () => buildMainPurposeSubline(t, result?.mainPurpose, result?.mainPurposeContext),
    [result?.mainPurpose, result?.mainPurposeContext, t],
  );
  const hasEditorErrors = Object.values(product.error).some((hasError) => hasError === true);
  const isCheckoutDisabled =
    !classmedPharmacyId || !product.quote || hasEditorErrors || product.data.length === 0 || product.isFormulaSaving;

  const handleTakeTestAgain = () => navigate(getClassmedQuizPath(classmedId, { pharmacyId: classmedPharmacyId, retake: true }));
  const handleScrollToReviews = () => reviewsRef.current?.scrollIntoView({ behavior: 'smooth' });

  const formulaLoader = showFormulaLoader ? (
    <FormulaLoader
      fromCache={!freshFromQuizRef.current && Boolean(result?.aiFromCache)}
      dataResolved={Boolean(result)}
      name={firstName}
      skipPending={freshFromQuizRef.current}
      onComplete={() => {
        setShowFormulaLoader(false);
        freshFromQuizRef.current = false;
      }}
    />
  ) : null;

  if (isFetching && !showFormulaLoader) {
    return (
      <div className="t-min-h-[100dvh] t-bg-surface t-font-sans t-text-navy">
        {noIndexHelmet}
        <Header showLanguageSelector={false} homePath={getClassmedResultPath(classmedId, { pharmacyId: classmedPharmacyId })} />
        <div className="t-flex t-min-h-[calc(100dvh-52px)] t-items-center t-justify-center t-px-5">
          <p className="!t-m-0 t-text-base t-font-semibold t-text-navy/70">{t('Classmed_ResultLoading')}</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="t-min-h-[100dvh] t-bg-surface t-font-sans t-text-navy">
        {noIndexHelmet}
        <Header showLanguageSelector={false} homePath={getClassmedResultPath(classmedId, { pharmacyId: classmedPharmacyId })} />
        <div className="t-flex t-min-h-[calc(100dvh-52px)] t-items-center t-justify-center t-px-5">
          <div className="t-w-full t-max-w-[460px] t-text-center">
            <img src={Logo} alt="" width={90} height={90} className="t-mx-auto t-mb-6" />
            <h1 className="!t-m-0 t-font-serif t-text-3xl t-font-normal">
              {t('Classmed_ResultUnavailableTitle')}
            </h1>
            <p className="!t-mt-3 !t-mb-0 t-text-base t-text-navy/70">
              {t('Classmed_ResultUnavailableBody')}
            </p>
          </div>
        </div>
        {formulaLoader}
      </div>
    );
  }

  return (
    <main className="t-relative t-bg-surface t-font-sans t-text-navy">
      {noIndexHelmet}
      <Header showLanguageSelector={false} homePath={getClassmedResultPath(classmedId, { pharmacyId: classmedPharmacyId })} />
      <div className="t-relative t-mx-auto t-flex t-w-full t-max-w-[1320px] t-flex-col t-gap-8 t-px-4 t-py-4 md:t-flex-row lg:t-h-screen lg:t-gap-0 lg:t-px-8">
        <div
          ref={leftContainerRef}
          className="hide-scrollbar t-w-full t-overflow-hidden sm:t-max-w-[300px] lg:t-sticky lg:t-top-0 lg:t-w-1/2 lg:t-max-w-none"
        >
          <Carousel
            data={product.data}
            allIngredients={product.allIngredients}
            onClickViewFormula={() => setIngredientsModal(true)}
            name={name}
            focusAreaCount={focusArea.length}
            personalSignalsCount={result.personalSignalsCount}
            specialConditionsCount={result.specialConditionsCount}
            medicationsCount={result.medicationsCount}
          />
        </div>

        <div
          ref={rightScrollRef}
          className="hide-scrollbar t-flex t-flex-col t-gap-3 sm:t-w-[calc(100%-300px)] lg:t-h-full lg:t-w-1/2 lg:t-overflow-y-auto lg:t-pl-8"
        >
          <div className="t-flex t-w-full t-flex-wrap t-items-center t-gap-2">
            <div className="t-flex t-flex-wrap t-items-center t-gap-1 sm:t-gap-2">
              <ReviewsOverlay
                totalReviews={reviewStats?.total ?? 0}
                averageReviews={reviewStats?.average ?? 0}
                onClick={handleScrollToReviews}
              />
              <div className="t-h-4 t-w-[2px] t-bg-navy/30" />
              <p className="!t-m-0 t-text-xs t-font-semibold t-text-navy/70 sm:t-text-[13px]">
                {t(ProductSuggestionDoses)}
              </p>
            </div>
            <button
              type="button"
              className="t-ml-auto t-rounded-md t-border t-border-navy/30 t-bg-transparent t-px-3 t-py-2 t-text-xs t-font-semibold t-text-navy t-transition-colors hover:t-border-navy hover:t-bg-navy/5"
              onClick={handleTakeTestAgain}
            >
              {t('Classmed_TakeTestAgain')}
            </button>
          </div>
          <p className="!t-m-0 t-text-xs t-font-bold t-text-[#116D6E] t-uppercase">
            {t('Classmed_ProductEyebrow')}
          </p>
          <h2 className="!t-m-0 t-font-serif t-font-normal t-leading-[0.98] t-text-navy">
            {name.trim()}, {t(PersonalFormulaReady)}
          </h2>
          {mainPurposeSubline && <p className="!t-m-0 t-text-[15px] t-text-navy/70">{mainPurposeSubline}</p>}
          <div className="t-flex t-flex-col t-gap-2">
            <ProductHighlightPill>{t(ProductSuggestionImprovementBadge)}</ProductHighlightPill>
            <ProductHighlightPill>
              {t(ProductSuggestionIngredientsBadge, { ingredientsCount: product.data.length })}
            </ProductHighlightPill>
          </div>
          <p className="!t-m-0 t-text-[15px] t-text-navy/70">{t(YourFocusAreasDesc)}</p>
          <FocusAreaAccordion
            focusArea={orderedFocusArea}
            focusAreaIcons={focusAreaIcons}
            collapsedCount={collapsedFocusCount}
          />
          <p className="!t-m-0 t-text-center t-text-sm t-italic t-text-g500 md:t-text-left">
            {t(VeganVCP)}
          </p>
          <ClassmedOneOffPurchaseCard
            prices={product.quote?.prices}
            selectedDuration={product.selectedDuration}
            onSelectDuration={product.setSelectedDuration}
            onCheckout={product.handleCheckout}
            isLoading={product.isCheckoutLoading}
            isDisabled={isCheckoutDisabled}
            focusArea={orderedFocusArea}
            ingredients={product.data}
          />
        </div>
      </div>

      <ProductPageSections
        lang={lang}
        rating={reviewStats?.average || '4.88'}
        reviewsRef={reviewsRef}
        excludedFaqTitleKeys={['Classmed_SubscriptionFaqTitle']}
      />

      <div className="t-fixed t-bottom-0 t-left-0 t-right-0 t-z-40 t-flex t-justify-center t-border-t t-border-g200 t-bg-surface/[.96] t-px-[18px] t-py-3.5 t-backdrop-blur-[10px] md:t-hidden">
        <button
          type="button"
          onClick={product.handleCheckout}
          disabled={isCheckoutDisabled || product.isCheckoutLoading}
          className="t-w-full t-max-w-[520px] t-rounded-full t-border-0 t-bg-quaternary t-px-6 t-py-[15px] t-text-base t-font-bold t-uppercase t-text-white hover:t-bg-[#2da898] disabled:t-cursor-not-allowed disabled:t-opacity-30"
        >
          {product.isCheckoutLoading
            ? t('Classmed_CheckoutLoading')
            : `${t('Classmed_CheckoutButton')} - ${
                product.quote?.prices?.[product.selectedDuration] == null
                  ? '...'
                  : formatClassmedPrice(product.quote.prices[product.selectedDuration])
              }`}
        </button>
      </div>

      <IngredientsModal
        open={ingredientsModal}
        handleClose={() => setIngredientsModal(false)}
        data={product.data}
        allIngredients={product.allIngredients}
        name={name}
        focusAreaCount={focusArea.length}
        personalSignalsCount={result.personalSignalsCount}
        specialConditionsCount={result.specialConditionsCount}
        medicationsCount={result.medicationsCount}
        showScientificName={false}
        onOpenEdit={() => {
          setIngredientsModal(false);
          setEditIngredientsModal(true);
        }}
      />
      <EditFormulaModal
        open={editIngredientsModal}
        handleClose={() => {
          setEditIngredientsModal(false);
          setIngredientsModal(true);
          product.handleFormulaEditComplete();
        }}
        data={product.data}
        ingredients={product.originalIngredients}
        hasFormulaChanged={product.hasFormulaChanged}
        setAddIngredientsModal={product.setAddIngredientsModal}
        allIngredients={product.allIngredients}
        onRemoveQuantity={product.onRemoveQuantity}
        onAddQuantity={product.onAddQuantity}
        prices={product.prices}
        removeIngredientFromBin={product.removeIngredientFromBin}
        onInputChange={product.onInputChange}
        onReset={product.resetFormula}
        name={name}
        showScientificName={false}
      />
      <AddIngredientModal
        open={product.addIngredientsModal}
        handleClose={() => product.setAddIngredientsModal(false)}
        data={product.data}
        removeIngredient={product.removeIngredient}
        addIngredient={product.addIngredient}
        allIngredients={product.allIngredients}
      />
      {formulaLoader}
    </main>
  );
};

export default ClassmedResult;
